import { useEffect } from "react";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { AlignLeft, ReceiptText } from "lucide-react";
import { TransactionSort, TransactionType } from "@boo/shared";
import type { Transaction, ExpenseCategory, IncomeCategory } from "@boo/shared";
import { dayjs } from "@boo/shared";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";
import { useAppStore, useCurrentMonth } from "../../store/useAppStore";
import { hasActiveFilter } from "../../types";
import { effectiveAmount } from "../../lib/effectiveAmount";
import UncategorizedSection from "./UncategorizedSection";
import TransactionItem from "./TransactionItem";
import StatementUploadButton from "./StatementUploadButton";
import SlipUploadButton from "./SlipUploadButton";
import TransactionsToolbar from "./TransactionsToolbar";
import BulkAssignAccountBar from "./BulkAssignAccountBar";

function formatDateHeader(dateStr: string, isToday: boolean): { left: string } {
  const d = dayjs(dateStr);
  const dow = d.format("ddd");
  const mon = d.format("MMM");
  const day = d.date();
  if (isToday) return { left: `today · ${day} ${mon} · ${dow}` };
  return { left: `${day} ${mon} · ${dow}` };
}

function groupByDate(transactions: Transaction[]) {
  const groups: Record<string, Transaction[]> = {};
  for (const tx of transactions) {
    if (!groups[tx.date]) groups[tx.date] = [];
    groups[tx.date].push(tx);
  }
  return groups;
}

function getDailyTotal(txs: Transaction[]): number {
  return txs.reduce(
    (sum, t) =>
      sum +
      (t.transactionType === TransactionType.EXPENSE &&
      t.cardStatementId == null
        ? effectiveAmount(t)
        : 0),
    0,
  );
}

export default function TransactionsCard() {
  const {
    transactions,
    transactionsLoading,
    transactionsTotal,
    transactionSort,
    transactionFilters,
    clearTransactionFilters,
    loadMoreTransactions,
    categorizeTransaction,
    setCardPayment,
    classifyIncoming,
    deleteTransaction,
    updateIncomeTransaction,
    renamePayee,
  } = useAppStore();
  const currentMonth = useCurrentMonth();
  const ensureAccounts = useAccountStore((s) => s.ensureAccounts);

  // Rows, the account filter and the pickers read account names.
  useEffect(() => {
    async function load() {
      try {
        await ensureAccounts();
      } catch {
        // Names are a hint; rows still render without them.
      }
    }
    void load();
  }, [ensureAccounts]);

  // Income transactions use `incomeCategory`, expense transactions use `category`.
  // Transfers have no category. Money received that is not classified yet
  // waits in the same box.
  const isUncategorized = (t: Transaction) =>
    t.needsReview ||
    (t.transactionType === TransactionType.INCOME
      ? t.incomeCategory === null
      : t.transactionType === TransactionType.EXPENSE && t.category === null);
  const hasFilter = hasActiveFilter(transactionFilters);
  // The "needs a category" box only exists in the default view; under a filter
  // or another sort, uncategorized rows stay in their place in the list.
  const isDefaultView =
    !hasFilter && transactionSort === TransactionSort.DateDesc;
  const isAmountSort =
    transactionSort === TransactionSort.AmountDesc ||
    transactionSort === TransactionSort.AmountAsc;

  const uncategorized = isDefaultView
    ? transactions.filter(isUncategorized)
    : [];
  const categorized = isDefaultView
    ? transactions.filter((t) => !isUncategorized(t))
    : transactions;

  const grouped = groupByDate(categorized);
  const sortedDates = Object.keys(grouped).sort((a, b) =>
    transactionSort === TransactionSort.DateAsc
      ? a.localeCompare(b)
      : b.localeCompare(a),
  );

  const todayDateStr = dayjs().format("YYYY-MM-DD");
  const remaining = transactionsTotal - transactions.length;
  const canLoadMore = remaining > 0 && !transactionsLoading;

  const handleCategorize = (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => {
    categorizeTransaction(id, category);
  };

  const renderItem = (tx: Transaction, showDate = false) => (
    <TransactionItem
      key={tx.id}
      transaction={tx}
      showDate={showDate}
      onCategorize={handleCategorize}
      onDelete={deleteTransaction}
      onSetCardPayment={setCardPayment}
      onClassifyIncoming={classifyIncoming}
      onUpdate={updateIncomeTransaction}
      onRenamePayee={renamePayee}
    />
  );

  return (
    <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-2 text-[15px] font-medium m-0 text-foreground whitespace-nowrap">
          <AlignLeft className="w-4 h-4 text-[var(--text-secondary)]" />
          Recent transactions
        </h2>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className="hidden sm:inline text-[11px] tracking-[0.04em] text-muted-foreground">
            {currentMonth?.month} · {transactionsTotal}{" "}
            {hasFilter ? "matching" : "total"}
          </span>
          <SlipUploadButton />
          <StatementUploadButton />
        </div>
      </div>

      <TransactionsToolbar />
      {transactionFilters.unassigned && <BulkAssignAccountBar />}

      {/* Uncategorized section */}
      {uncategorized.length > 0 && (
        <UncategorizedSection
          transactions={uncategorized}
          onCategorize={handleCategorize}
          onClassifyIncoming={classifyIncoming}
          onDelete={deleteTransaction}
        />
      )}

      {/* Amount sort: flat list, ranked across the whole month */}
      {isAmountSort && categorized.length > 0 && (
        <div className="flex flex-col divide-y divide-border sm:divide-y-0 sm:gap-2 mt-3.5">
          {categorized.map((tx) => renderItem(tx, true))}
        </div>
      )}

      {/* Date sort: transactions grouped by date */}
      {!isAmountSort &&
        sortedDates.map((date) => {
          const dayTxs = grouped[date];
          const isToday = date === todayDateStr;
          const header = formatDateHeader(date, isToday);
          const dailyTotal = getDailyTotal(dayTxs);

          return (
            <div key={date}>
              {/* Date header */}
              <div className="flex items-baseline justify-between mb-0.5 mt-5 sm:mb-1.5 sm:mt-3.5">
                <span className="text-[11px] font-medium tracking-[0.08em] text-[var(--text-secondary)]">
                  {header.left}
                </span>
                {!hasFilter && (
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    ฿{dailyTotal.toLocaleString("en-US")} spent
                  </span>
                )}
              </div>
              {/* Transaction list */}
              <div className="flex flex-col divide-y divide-border sm:divide-y-0 sm:gap-2">
                {dayTxs.map((tx) => renderItem(tx))}
              </div>
            </div>
          );
        })}

      {/* First-load skeleton */}
      {transactionsTotal === 0 && transactionsLoading && (
        <SkeletonRegion className="flex flex-col gap-2 py-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-[52px] rounded-[10px]" />
          ))}
        </SkeletonRegion>
      )}

      {/* Empty state */}
      {transactionsTotal === 0 && !transactionsLoading && hasFilter && (
        <div className="flex flex-col items-center gap-2.5 py-8 text-center">
          <ReceiptText className="w-8 h-8 text-muted-foreground opacity-40" />
          <p className="text-[13px] text-muted-foreground">
            No transactions match
          </p>
          <button
            type="button"
            onClick={() => clearTransactionFilters()}
            className="text-[12px] font-medium px-3 py-1.5 rounded-[10px] transition-colors cursor-pointer bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)]"
          >
            Clear filters
          </button>
        </div>
      )}
      {transactionsTotal === 0 && !transactionsLoading && !hasFilter && (
        <div className="flex flex-col items-center gap-2.5 py-8 text-center">
          <ReceiptText className="w-8 h-8 text-muted-foreground opacity-40" />
          <p className="text-[13px] text-muted-foreground">
            No transactions in {currentMonth?.month}
          </p>
          <p className="text-[11px] text-[var(--text-secondary)] opacity-70">
            Add your first expense to start tracking
          </p>
        </div>
      )}

      {/* Load more button */}
      {transactionsTotal > 0 && (
        <div className="flex justify-center mt-4.5 pt-1">
          <button
            disabled={!canLoadMore}
            className="text-[13px] font-medium px-5 py-2.5 rounded-[10px] transition-colors cursor-pointer bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => loadMoreTransactions()}
          >
            {transactionsLoading ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="size-3.5" />
                Loading…
              </span>
            ) : remaining > 0 ? (
              `${isDefaultView ? "Load earlier days" : "Load more"} · ${remaining} more`
            ) : isDefaultView ? (
              "All caught up · no earlier transactions"
            ) : (
              "All caught up · no more transactions"
            )}
          </button>
        </div>
      )}
    </div>
  );
}
